/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — Tab Movimentação Financeira (demonstrativo + analytics)
 * Data: 2026-06-14
 */
import { useEffect, useState } from 'react'
import { useObraArea } from '../hooks/useObraAnalytics'
import { DETAIL_CONFIG } from '../config/areaAnalytics'
import { formatCurrency, formatPercent } from '../../../../utils/obraAnalytics'
import KpiCard from '../../../../Components/Analytics/KpiCard'
import MetricCard from '../components/MetricCard'
import DemonstrativoMensal, { CHAVE_DEMONSTRATIVO_PERIODO } from '../components/DemonstrativoMensal'
import EvolucaoContaMensal from '../components/EvolucaoContaMensal'
import MovimentacaoFluxoDiario from '../components/MovimentacaoFluxoDiario'
import EvolucaoContaChart from '../components/EvolucaoContaChart'
import DrillDownTable from '../components/DrillDownTable'
import TabState from '../components/TabState'

const ResultadoColumn = ({ titulo, cor, itens, total }) => (
    <div className="p-4 space-y-2">
        <p className={`text-xs font-bold uppercase tracking-wide mb-3 ${cor}`}>{titulo}</p>
        {itens.length === 0 ? (
            <p className="text-xs text-gray-400">Nenhum lançamento.</p>
        ) : (
            <>
                {itens.map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                        <span className="text-xs text-gray-600 truncate max-w-[60%]">{item.label ?? item.categoria}</span>
                        <div className="text-right">
                            <span className={`text-xs font-semibold ${cor}`}>{formatCurrency(item.total)}</span>
                            {item.quantidade > 0 && <span className="text-xs text-gray-400 ml-2">({item.quantidade})</span>}
                        </div>
                    </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t mt-2">
                    <span className="text-xs font-bold text-gray-700">Total</span>
                    <span className={`text-xs font-bold ${cor}`}>{formatCurrency(total)}</span>
                </div>
            </>
        )}
    </div>
)

const formatRefDate = (iso) => {
    if (!iso) return '—'
    const [y, m, d] = iso.split('-')
    return `${d}/${m}/${y}`
}

const MovimentacaoTab = ({ obraId, investidor, period }) => {
    const { data, loading, error } = useObraArea(obraId, 'movimentacao', { inicio: period.inicio, fim: period.fim }, investidor)
    const [mesSelecionado, setMesSelecionado] = useState(null)
    const [mostrarAnalitico, setMostrarAnalitico] = useState(false)

    useEffect(() => {
        if (!data) return
        const meses = data.demonstrativos || []
        const temPeriodo = !!data.demonstrativo_periodo
        const padrao = temPeriodo
            ? (data.demonstrativo_periodo.chave || CHAVE_DEMONSTRATIVO_PERIODO)
            : (data.demonstrativo?.mes_ano || meses[meses.length - 1]?.mes_ano || null)
        setMesSelecionado((atual) => {
            if (atual === CHAVE_DEMONSTRATIVO_PERIODO && temPeriodo) return atual
            if (atual && meses.some((m) => m.mes_ano === atual)) return atual
            return padrao
        })
    }, [data])

    if (loading || error || !data) return <TabState loading={loading} error={error} />

    const c = data.cards
    const dre = data.dre || {
        receitas: [],
        despesas: [],
        impostos: [],
        totalReceitas: 0,
        totalDespesas: 0,
        totalImpostos: 0,
        resultado: 0,
    }
    const serieDiaria = data.serieDiaria || []
    const evolucaoConta = data.evolucaoConta || serieDiaria
    const serieMensal = data.serieMensal || []
    const demonstrativos = data.demonstrativos || []
    const demonstrativoPeriodo = data.demonstrativo_periodo || null
    const ehPeriodo = mesSelecionado === CHAVE_DEMONSTRATIVO_PERIODO
        || (!mesSelecionado && !!demonstrativoPeriodo)
    const demonstrativoAtivo = ehPeriodo
        ? (demonstrativoPeriodo || data.demonstrativo)
        : (demonstrativos.find((d) => d.mes_ano === mesSelecionado) || demonstrativoPeriodo || data.demonstrativo)
    const janela = data.janela || {}
    const periodoTotais = data.periodo || {}
    const refDia = janela.ref || period.fim

    const receitasPeriodo = periodoTotais.receitas ?? 0
    const despesasPeriodo = periodoTotais.despesas ?? 0
    const impostosPeriodo = periodoTotais.impostos ?? dre.totalImpostos ?? 0
    const resultadoPeriodo = periodoTotais.resultado
        ?? demonstrativoPeriodo?.resultado
        ?? 0
    const margem = receitasPeriodo > 0
        ? parseFloat(((resultadoPeriodo / receitasPeriodo) * 100).toFixed(1))
        : 0
    const periodoLabel = periodoTotais.label
        || (period.inicio && period.fim
            ? `${formatRefDate(period.inicio)} a ${formatRefDate(period.fim)}`
            : null)

    const receitasCard = ehPeriodo
        ? receitasPeriodo
        : (demonstrativoAtivo?.receitas?.total ?? c.total_receitas)
    const despesasCard = ehPeriodo
        ? despesasPeriodo
        : (demonstrativoAtivo?.despesas?.total ?? c.total_despesas)
    const impostosCard = ehPeriodo
        ? impostosPeriodo
        : (demonstrativoAtivo?.impostos?.total ?? c.total_impostos ?? 0)
    const resultadoCard = ehPeriodo
        ? resultadoPeriodo
        : (demonstrativoAtivo?.resultado
            ?? demonstrativoAtivo?.movimento_liquido
            ?? c.movimento_liquido_mes
            ?? 0)
    const escopoLabel = ehPeriodo
        ? (periodoLabel || 'Período')
        : (demonstrativoAtivo?.label || 'Mês')

    const saldoInicial = demonstrativoAtivo?.saldo_inicial?.valor ?? c.saldo_inicial_mes
    const saldoFinal = demonstrativoAtivo?.saldo_final?.valor ?? c.saldo_final_mes

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <MetricCard
                    title="Saldo inicial"
                    value={saldoInicial}
                    format={formatCurrency}
                    subtitle={demonstrativoAtivo ? `Em ${formatRefDate(demonstrativoAtivo.saldo_inicial?.data)}` : (ehPeriodo ? 'Início do período' : 'Início do mês')}
                />
                <MetricCard
                    title={ehPeriodo ? 'Receitas' : 'Receitas do mês'}
                    value={receitasCard}
                    format={formatCurrency}
                    subtitle={escopoLabel}
                />
                <MetricCard
                    title={ehPeriodo ? 'Despesas' : 'Despesas do mês'}
                    value={despesasCard}
                    format={formatCurrency}
                    subtitle={escopoLabel}
                />
                <KpiCard
                    title="Saldo final"
                    value={formatCurrency(saldoFinal ?? c.saldo_conta_atual)}
                    subtitle={
                        resultadoCard != null
                            ? `Resultado ${formatCurrency(resultadoCard)}`
                            : 'Saldo operacional'
                    }
                />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">
                <MetricCard
                    title={ehPeriodo ? 'Impostos' : 'Impostos do mês'}
                    value={impostosCard}
                    format={formatCurrency}
                    subtitle={escopoLabel}
                />
                <MetricCard
                    title={ehPeriodo ? 'Resultado' : 'Resultado do mês'}
                    value={resultadoCard}
                    format={formatCurrency}
                    subtitle="DRE financeiro (saldo depois − saldo antes)"
                />
            </div>

            <DemonstrativoMensal
                demonstrativo={data.demonstrativo}
                demonstrativoPeriodo={demonstrativoPeriodo}
                demonstrativos={demonstrativos}
                mesSelecionado={mesSelecionado}
                onChangeMes={setMesSelecionado}
            />

            <EvolucaoContaMensal
                title="Evolução da conta no período"
                serie={serieMensal}
                mesDestaque={ehPeriodo ? null : mesSelecionado}
            />

            <div className="border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm">
                <button
                    type="button"
                    onClick={() => setMostrarAnalitico((v) => !v)}
                    className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                    <p className="text-sm font-bold text-gray-800">Detalhamento analítico</p>
                    <span className="text-xs font-semibold text-rose-700 bg-rose-50 px-3 py-1 rounded-full">
                        {mostrarAnalitico ? 'Ocultar' : 'Expandir'}
                    </span>
                </button>

                {mostrarAnalitico && (
                    <div className="px-5 pb-5 space-y-6 border-t border-gray-100 pt-5">
                        {periodoLabel && (
                            <p className="text-xs text-gray-600 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2">
                                Período: <b>{periodoLabel}</b>
                            </p>
                        )}

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <MetricCard
                                title="Receitas (período)"
                                value={receitasPeriodo}
                                format={formatCurrency}
                                subtitle={periodoLabel || 'Período selecionado'}
                            />
                            <MetricCard
                                title="Despesas (período)"
                                value={despesasPeriodo}
                                format={formatCurrency}
                                subtitle={periodoLabel || 'Período selecionado'}
                            />
                            <MetricCard
                                title="Impostos (período)"
                                value={impostosPeriodo}
                                format={formatCurrency}
                                subtitle={periodoLabel || 'Período selecionado'}
                            />
                            <MetricCard
                                title="Resultado (período)"
                                value={resultadoPeriodo}
                                format={formatCurrency}
                                subtitle="DRE financeiro (saldo depois − saldo antes)"
                            />
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <MetricCard
                                title="Margem (período)"
                                value={margem}
                                format={formatPercent}
                                subtitle="Sobre as receitas"
                            />
                            <MetricCard
                                title="Receitas (passado)"
                                value={c.receitas_passado}
                                format={formatCurrency}
                                subtitle={`Até ${formatRefDate(refDia)}`}
                            />
                            <MetricCard
                                title="Despesas (passado)"
                                value={c.despesas_passado}
                                format={formatCurrency}
                                subtitle={`Até ${formatRefDate(refDia)}`}
                            />
                            <MetricCard
                                title="Receitas (futuro)"
                                value={c.receitas_futuro}
                                format={formatCurrency}
                                subtitle={`Após ${formatRefDate(refDia)}`}
                            />
                        </div>

                        <MovimentacaoFluxoDiario title="Receitas x Despesas — dia a dia" serie={serieDiaria} refDia={refDia} />

                        <EvolucaoContaChart title="Evolução diária do saldo" serie={evolucaoConta} refDia={refDia} />

                        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100">
                                <p className="text-sm font-bold text-gray-800">Detalhe operacional (Receitas, Despesas, Impostos)</p>
                                <p className="text-[11px] text-gray-400 mt-0.5">Não somam o Resultado — faltam tarifas CTRL e custas no caixa.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                                <ResultadoColumn titulo="Receitas" cor="text-emerald-700" itens={dre.receitas || []} total={dre.totalReceitas} />
                                <ResultadoColumn titulo="Despesas" cor="text-rose-700" itens={dre.despesas || []} total={dre.totalDespesas} />
                                <ResultadoColumn titulo="Impostos" cor="text-amber-700" itens={dre.impostos || []} total={dre.totalImpostos ?? 0} />
                            </div>
                        </div>

                        <DrillDownTable obraId={obraId} area="movimentacao" investidor={investidor} config={DETAIL_CONFIG.movimentacao} period={period} showSearch={false} />
                    </div>
                )}
            </div>
        </div>
    )
}

export default MovimentacaoTab
